import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TopNav } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, LogIn, Users, Upload, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { warn } from "@/lib/logger";

export default function Dashboard() {
  const [rooms, setRooms] = useState([]);
  const [listoneCount, setListoneCount] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joinTeam, setJoinTeam] = useState("");
  const [showJoin, setShowJoin] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [{ data: rs }, { data: info }] = await Promise.all([
        api.get("/rooms"),
        api.get("/listone/info"),
      ]);
      setRooms(rs);
      setListoneCount(info?.count ?? 0);
    } catch (err) {
      warn("dashboard load failed:", err);
      toast.error("Errore caricamento sale");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doJoin = async () => {
    try {
      const { data } = await api.post("/rooms/join", { code: joinCode.toUpperCase(), team_name: joinTeam });
      toast.success("Entrato nella sala!");
      nav(`/rooms/${data.id}/lobby`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <TopNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="label-eyebrow mb-2">Il tuo tavolo</div>
            <h1 className="text-4xl font-black tracking-tight">Le mie sale d'asta</h1>
            <p className="text-slate-400 mt-2">Crea una nuova sala o unisciti con un codice invito.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="btn-toggle-join" variant="outline" onClick={() => setShowJoin(!showJoin)} className="border-emerald-500/30 hover:bg-emerald-500/10">
              <LogIn className="w-4 h-4 mr-2" />Entra con codice
            </Button>
            <Link to="/rooms/new">
              <Button data-testid="btn-create-room" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                <Plus className="w-4 h-4 mr-2" />Crea sala
              </Button>
            </Link>
          </div>
        </div>

        {showJoin && (
          <div className="glass rounded-2xl p-5 mb-8 flex flex-col sm:flex-row gap-3" data-testid="join-form">
            <Input data-testid="join-code-input" placeholder="Codice sala (es. AB12CD)" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="bg-slate-900/60 border-white/10 h-11 uppercase" maxLength={6} />
            <Input data-testid="join-team-input" placeholder="Nome della tua squadra" value={joinTeam} onChange={(e) => setJoinTeam(e.target.value)} className="bg-slate-900/60 border-white/10 h-11" />
            <Button data-testid="join-submit" onClick={doJoin} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11 px-6">
              Unisciti
            </Button>
          </div>
        )}

        {/* Listone status banner — always visible for registered creators */}
        {listoneCount !== null && (
          listoneCount === 0 ? (
            <div className="rounded-2xl p-5 mb-8 border border-amber-500/40 bg-amber-500/5 flex flex-col sm:flex-row sm:items-center gap-4"
                 data-testid="dashboard-listone-banner-empty">
              <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black">Carica il Listone per iniziare</div>
                <div className="text-sm text-slate-400">Senza listone non puoi creare sale d'asta. Supportato il file ufficiale di Fantacalcio.it (CSV con <code>;</code> o Excel).</div>
              </div>
              <Link to="/admin/listone">
                <Button data-testid="dashboard-upload-listone" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black h-11 px-5 whitespace-nowrap">
                  <Upload className="w-4 h-4 mr-2" />Carica Listone
                </Button>
              </Link>
            </div>
          ) : (
            <div className="rounded-2xl p-4 mb-8 border border-emerald-500/30 bg-emerald-500/5 flex items-center gap-3"
                 data-testid="dashboard-listone-banner-ok">
              <FileSpreadsheet className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <div className="flex-1 text-sm">
                <b className="text-emerald-400">Listone pronto:</b> <span className="font-mono font-bold">{listoneCount}</span> calciatori disponibili per le aste.
              </div>
              <Link to="/admin/listone">
                <Button data-testid="dashboard-manage-listone" variant="outline" size="sm" className="border-emerald-500/30 hover:bg-emerald-500/10 text-emerald-400">
                  Gestisci
                </Button>
              </Link>
            </div>
          )
        )}

        {rooms.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center" data-testid="rooms-empty">
            <Users className="w-12 h-12 mx-auto text-emerald-400 mb-3" />
            <div className="text-xl font-bold mb-1">Nessuna sala attiva</div>
            <div className="text-slate-400 mb-4">Crea la tua prima sala d'asta o entra con un codice.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rooms-grid">
            {rooms.map((r) => (
              <Link key={r.id} to={r.phase === "lobby" ? `/rooms/${r.id}/lobby` : `/rooms/${r.id}/live`}
                    data-testid={`room-card-${r.code}`}
                    className="glass rounded-2xl p-5 hover:border-emerald-500/50 hover:bg-slate-900/80 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Codice</div>
                    <div className="font-mono font-black text-emerald-400 text-xl">{r.code}</div>
                  </div>
                  {(() => {
                    const phaseStyle = {
                      live: "bg-red-500/20 text-red-400",
                      finished: "bg-slate-700/30 text-slate-400",
                      lobby: "bg-emerald-500/20 text-emerald-400",
                    }[r.phase] || "bg-emerald-500/20 text-emerald-400";
                    const phaseLabel = { lobby: "Lobby", live: "LIVE", finished: "Chiusa" }[r.phase] || "Chiusa";
                    return (
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-md ${phaseStyle}`}>
                        {phaseLabel}
                      </span>
                    );
                  })()}
                </div>
                <div className="font-bold text-lg mb-2 group-hover:text-emerald-400 transition-colors">{r.name}</div>
                <div className="text-sm text-slate-400">{r.teams.length} squadre • {r.initial_credits} FM</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
